import { useRef } from 'react';
import { Box, Button, Text, Title } from '@nimbus-ds/components';
import {
  MoneyIcon,
  CheckIcon,
  CloseIcon,
  PaperPlaneIcon,
  UserGroupIcon,
} from '@nimbus-ds/icons';
import { InitialScreen, PlanDisplay } from '@nimbus-ds/patterns';
import { MARKETING_AUTOMATION_APP_INSTALL_LINK } from 'commons/constants/externalLinks';
import { useTranslationLanguage } from 'commons/hooks';
import { openWindow } from 'commons/utils/window';
import {
  useNuvemMarketingPlans,
  useTranslationMarketing,
} from 'domains/Marketing/hook';
import DashboardImgEsAr from './images/dashboard-nuvem-marketing-es-ar.png';
import DashboardImgEsMx from './images/dashboard-nuvem-marketing-es-mx.png';
import DashboardImgPtBr from './images/dashboard-nuvem-marketing-pt-br.png';
import HeroImg from './images/hero-nuvem-marketing.png';
import { trackingMarketingAutomationAppInstallation } from '../tracking';

const dashboardImgMap = {
  'es-AR': DashboardImgEsAr,
  'es-MX': DashboardImgEsMx,
  'es-CO': DashboardImgEsMx,
  'es-CL': DashboardImgEsMx,
  'pt-BR': DashboardImgPtBr,
};

export function NuvemMarketingPage() {
  const t = useTranslationMarketing('nuvemMarketing');
  const language = useTranslationLanguage();
  const { freePlan, monthlyPlan } = useNuvemMarketingPlans();

  const handleInstallApplicationClick = () => {
    const url = MARKETING_AUTOMATION_APP_INSTALL_LINK[language];
    trackingMarketingAutomationAppInstallation();
    openWindow(url, true);
  };

  const targetElementRef = useRef<HTMLDivElement>(null);

  const handleScrollClick = () => {
    if (targetElementRef.current) {
      targetElementRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <InitialScreen mx="auto" mb="10">
      {/* Hero */}
      <InitialScreen.Hero
        subtitle={t('hero.subtitle').toUpperCase()}
        title={t('hero.title')}
        description={t('hero.description')}
        actions={
          <>
            <Button
              appearance="primary"
              onClick={handleInstallApplicationClick}
            >
              {t('hero.button')}
            </Button>
            <Button appearance="neutral" onClick={handleScrollClick}>
              {t('hero.seeMore')}
            </Button>
          </>
        }
        image={
          <img
            src={HeroImg}
            alt={t('hero.altImage')}
            width="100%"
            height="auto"
          />
        }
      />
      {/* Content Cards */}
      <div ref={targetElementRef}>
        <InitialScreen.Section title={t('contentCards.title')}>
          <InitialScreen.CardLayout>
            <InitialScreen.Card
              icon={<MoneyIcon size="large" />}
              backgroundColor="neutral-background"
              title={t('contentCards.card1.title')}
              description={t('contentCards.card1.description')}
            />

            <InitialScreen.Card
              icon={<UserGroupIcon size="large" />}
              backgroundColor="neutral-background"
              title={t('contentCards.card2.title')}
              description={t('contentCards.card2.description')}
            />
            <InitialScreen.Card
              icon={<PaperPlaneIcon size="large" />}
              backgroundColor="neutral-background"
              title={t('contentCards.card3.title')}
              description={t('contentCards.card3.description')}
            />
          </InitialScreen.CardLayout>
        </InitialScreen.Section>
      </div>

      {/* Content Section */}
      <InitialScreen.Section title={t('contentSection.title')}>
        <InitialScreen.Feature
          content={
            <>
              <InitialScreen.FeatureItem
                title={t('contentSection.content1.title')}
                description={t('contentSection.content1.description')}
              >
                <InitialScreen.FeatureItemSpacing />
              </InitialScreen.FeatureItem>

              <InitialScreen.FeatureItem
                title={t('contentSection.content2.title')}
                description={t('contentSection.content2.description')}
              >
                <InitialScreen.FeatureItemSpacing />
              </InitialScreen.FeatureItem>
              <InitialScreen.FeatureItem
                title={t('contentSection.content3.title')}
                description={t('contentSection.content3.description')}
              >
                <InitialScreen.FeatureItemSpacing />
              </InitialScreen.FeatureItem>
              <InitialScreen.FeatureItem
                title={t('contentSection.content4.title')}
                description={t('contentSection.content4.description')}
              />
            </>
          }
          image={
            <Box
              maxWidth="500px"
              borderRadius="6"
              borderColor="primary-interactive"
              borderWidth="4"
              borderStyle="solid"
            >
              <img
                src={dashboardImgMap[language]}
                alt={t('contentSection.altImage')}
                width="100%"
                style={{ display: 'grid' }}
              />
            </Box>
          }
        />
      </InitialScreen.Section>
      {/* Plans Section */}
      <InitialScreen.Section title={t('plansSection.title')} gridGap="4">
        <Text color="neutral-textLow">{t('plansSection.subtitle')}</Text>
        <PlanDisplay minWidth={{ xs: '150px', md: '500px' }} mx="4" mt="2">
          {/* Plan Free */}
          <PlanDisplay.Card highlighted>
            <PlanDisplay.Header
              subtitle={t('plansSection.plans.free.label')}
              title={
                <Box display="flex" gap="1">
                  <Title as="h3" color="neutral-textLow">
                    {t('plansSection.plans.title')}
                  </Title>
                  <Title as="h3" color="neutral-textHigh">
                    {t('plansSection.plans.free.title')}
                  </Title>
                </Box>
              }
            />
            <PlanDisplay.Content>
              {freePlan.map(({ feature, disabled }) => (
                <PlanDisplay.Bullet
                  icon={disabled ? <CloseIcon /> : <CheckIcon />}
                  disabled={disabled}
                  key={feature}
                >
                  {feature}
                </PlanDisplay.Bullet>
              ))}
            </PlanDisplay.Content>
            <PlanDisplay.Footer>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                width="100%"
                gap="2"
                mb="2"
              >
                <Button
                  appearance="primary"
                  onClick={handleInstallApplicationClick}
                >
                  <Text fontSize="base" color="currentColor">
                    {t('plansSection.plans.free.button')}
                  </Text>
                </Button>
              </Box>
            </PlanDisplay.Footer>
          </PlanDisplay.Card>
          {/* Plan Monthly */}
          <PlanDisplay.Card>
            <PlanDisplay.Header
              subtitle={t('plansSection.plans.monthly.label')}
              title={
                <Box display="flex" gap="1">
                  <Title as="h3" color="neutral-textLow">
                    {t('plansSection.plans.title')}
                  </Title>
                  <Title as="h3" color="neutral-textHigh">
                    {t('plansSection.plans.monthly.title')}
                  </Title>
                </Box>
              }
            />
            <PlanDisplay.Content>
              {monthlyPlan.map(({ feature, disabled }) => (
                <PlanDisplay.Bullet
                  icon={disabled ? <CloseIcon /> : <CheckIcon />}
                  disabled={disabled}
                  key={feature}
                >
                  {feature}
                </PlanDisplay.Bullet>
              ))}
            </PlanDisplay.Content>
            <PlanDisplay.Footer>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                width="100%"
                gap="2"
              >
                <Box height="3rem" />
              </Box>
            </PlanDisplay.Footer>
          </PlanDisplay.Card>
        </PlanDisplay>
      </InitialScreen.Section>
    </InitialScreen>
  );
}
